// libs
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

// context
import { useLoginNudgeContext } from './context';
import { useListingApiContext } from 'shared/layouts/Listing/Api/Context';

// constants
import { NUDGE_SOURCES } from './constants';

// helpers
import { getEligibleVariantName } from 'shared/components/ExperimentV2/helpers';

// types
interface LoginNudgeTrackerProps {
  experimentName: string;
  sessionTimeInHours: number;
  pageType: string;
  minimumFiltersForTracking?: number;
}

/**
 * LoginNudgeTracker Component
 * 
 * Handles automatic tracking of user interactions for login nudge A/B testing
 * Works on both mobile and desktop web
 * 
 * Usage:
 * - PLP (Product Listing Page): Tracks URL changes (listing loads) and filter applications
 * - PDP (Product Detail Page): Tracks product views
 * 
 * @param experimentName - Name of the A/B test experiment
 * @param sessionTimeInHours - Session time configuration for nudge
 * @param pageType - Type of page (PAGENAME.PLP or 'pdp')
 * @param minimumFiltersForTracking - Minimum filter count to trigger tracking (default: 2)
 */
export default function LoginNudgeTracker({
  sessionTimeInHours = 24,
  pageType,
  minimumFiltersForTracking = 1,
}: LoginNudgeTrackerProps) {
  const location = useLocation();
  const previousQueryRef = useRef<string>('');
  const isFirstRenderRef = useRef<boolean>(true);
  const pdpDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastPDPTrackTimeRef = useRef<number>(0);
  const { showNudge, trackListingLoad, trackFilterApplied, trackPDPView } = useLoginNudgeContext();
  
  // Get experiment variant
  const { isEligible: isEnabled = false, variant = '' } = getEligibleVariantName("login-experience") || {};
  
  // Get filter data for PLP pages
  const listingContext = pageType.startsWith('plp') ? useListingApiContext() : null;
  const displayAppliedFilters = listingContext?.displayAppliedFilters || [];

  // Initialize showNudge on mount
  useEffect(() => {
    if (isEnabled) {
      variant !== 'login-experience-c' && showNudge(sessionTimeInHours, NUDGE_SOURCES.NUDGE_VISIBLE, variant);
    }
  }, [isEnabled, sessionTimeInHours, variant, showNudge]);
  // Track URL changes for PLP (listing loads and filter applications)
  useEffect(() => {
    if (!isEnabled || !pageType.startsWith('plp')) return;
    
    const currentQuery = location.search;
    const currentFilterCount = displayAppliedFilters.length;
    const hasQueryChanged = previousQueryRef.current !== currentQuery;
    
    // Track listing load on first render OR when query params change
    if (isFirstRenderRef.current || hasQueryChanged) {
      trackListingLoad();
      
      // Track filter application if:
      // - Not the first render (only track on subsequent filter applications)
      // - Filter count meets minimum threshold
      // - Query doesn't contain "search" parameter
      const hasSearchParam = currentQuery.includes('showsearch');
      if (!isFirstRenderRef.current && !hasSearchParam && currentFilterCount >= minimumFiltersForTracking) {
        trackFilterApplied(currentFilterCount);
      }
      
      // Mark that first render is complete
      isFirstRenderRef.current = false;
    }
    
    // Update the previous query ref
    previousQueryRef.current = currentQuery;
  }, [
    location.search,
    isEnabled,
    pageType,
    trackListingLoad,
    trackFilterApplied,
    displayAppliedFilters.length,
    minimumFiltersForTracking,
  ]);

  // Track URL changes for PDP (product views) with debouncing
  useEffect(() => {
    if (!isEnabled || !pageType.startsWith('pdp')) return;

    // Clear any existing timer
    if (pdpDebounceTimerRef.current) {
      clearTimeout(pdpDebounceTimerRef.current);
    }

    // Set debounce timer
    pdpDebounceTimerRef.current = setTimeout(() => {
      const now = Date.now();
      const timeSinceLastTrack = now - lastPDPTrackTimeRef.current;
      
      // Only track if at least 1 second has passed since last track
      if (timeSinceLastTrack >= 1000) {
        trackPDPView();
        lastPDPTrackTimeRef.current = now;
      }
    }, 1000);

    // Cleanup on unmount
    return () => {
      if (pdpDebounceTimerRef.current) {
        clearTimeout(pdpDebounceTimerRef.current);
      }
    };
  }, [location.pathname, isEnabled, pageType, trackPDPView]);

  // This component doesn't render anything
  return null;
}
